import Fuse from "fuse.js";

import { logger } from "@/src/lib/logging/logger";
import { areDirectNicknames, directlyRelatedGivenNames } from "@/src/lib/name-matching/nicknames";
import { soundsAlike } from "@/src/lib/name-matching/pronunciation";

export type NameQuery = {
  givenName?: string | null;
  middleName?: string | null;
  familyName?: string | null;
};

type PreparedNameQuery = {
  normalizedGivenName: string;
  normalizedFamilyName: string;
  normalizedFullName: string;
  givenNameTokens: string[];
  familyNameTokens: string[];
  fullNameTokens: string[];
};

export enum FuzzyNameMatchThreshold {
  Strict = 0.2,
  // Use with caution — only after exact DOB + phone evidence has narrowed matching to one candidate.
  Loose = 0.5,
}

export function normalizeName(s: string | null | undefined): string {
  if (!s) return "";

  return s
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeFullName(person: NameQuery): string {
  return [person.givenName, person.middleName, person.familyName]
    .map((part) => normalizeName(part))
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function isExactNameMatch(p: NameQuery, query: NameQuery): boolean {
  return (
    normalizeName(p.givenName) === normalizeName(query.givenName) &&
    normalizeName(p.familyName) === normalizeName(query.familyName)
  );
}

function tokensFromNormalizedName(value: string): string[] {
  return value.split(" ").filter(Boolean);
}

function prepareNameQuery(query: NameQuery): PreparedNameQuery {
  const normalizedGivenName = normalizeName(query.givenName);
  const normalizedMiddleName = normalizeName(query.middleName);
  const normalizedFamilyName = normalizeName(query.familyName);
  const normalizedFullName = [normalizedGivenName, normalizedMiddleName, normalizedFamilyName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    normalizedGivenName,
    normalizedFamilyName,
    normalizedFullName,
    givenNameTokens: tokensFromNormalizedName(normalizedGivenName),
    familyNameTokens: tokensFromNormalizedName(normalizedFamilyName),
    fullNameTokens: tokensFromNormalizedName(normalizedFullName),
  };
}

function isExactNameMatchPrepared(p: NameQuery, preparedQuery: PreparedNameQuery): boolean {
  return (
    normalizeName(p.givenName) === preparedQuery.normalizedGivenName &&
    normalizeName(p.familyName) === preparedQuery.normalizedFamilyName
  );
}

function tokenSetContainsAll(container: string[], contained: string[]): boolean {
  return contained.length > 0 && contained.every((token) => container.includes(token));
}

function oneNameSideContainsTheOther(left: string[], right: string[]): boolean {
  if (left.length === 0 || right.length === 0) return false;

  return left.length >= right.length ? tokenSetContainsAll(left, right) : tokenSetContainsAll(right, left);
}

function compoundNameFieldsMatchPrepared(candidate: NameQuery, preparedQuery: PreparedNameQuery): boolean {
  const normalizedCandidateGivenName = normalizeName(candidate.givenName);
  const normalizedCandidateFamilyName = normalizeName(candidate.familyName);
  const givenNameExactMatch = normalizedCandidateGivenName === preparedQuery.normalizedGivenName;
  const familyNameExactMatch = normalizedCandidateFamilyName === preparedQuery.normalizedFamilyName;

  return (
    (familyNameExactMatch &&
      oneNameSideContainsTheOther(
        preparedQuery.givenNameTokens,
        tokensFromNormalizedName(normalizedCandidateGivenName)
      )) ||
    (givenNameExactMatch &&
      oneNameSideContainsTheOther(
        preparedQuery.familyNameTokens,
        tokensFromNormalizedName(normalizedCandidateFamilyName)
      ))
  );
}

/**
 * Returns whether every query token fuzzy-matches at least one token in storedName,
 * along with per-token scores useful for diagnostic logging.
 */
function allQueryTokensMatch(
  queryTokens: string[],
  storedName: string,
  threshold: number = FuzzyNameMatchThreshold.Strict
): { matched: boolean; scores: (number | undefined)[] } {
  const storedTokens = storedName.split(" ").filter(Boolean);
  const fuse = new Fuse(storedTokens, {
    includeScore: true,
    threshold: 1,
    isCaseSensitive: false,
  });

  const tokenResults = queryTokens.map((qt) => fuse.search(qt));
  const scores = tokenResults.map((results) => results[0]?.score);
  const matched = scores.every((score) => score !== undefined && score <= threshold);

  return { matched, scores };
}

/** Matches one candidate with the shared Fuse policy without logging PHI-bearing names or scores. */
export function isFuzzyNameMatch(
  candidate: NameQuery,
  query: NameQuery,
  threshold = FuzzyNameMatchThreshold.Strict
): boolean {
  const preparedQuery = prepareNameQuery(query);

  if (preparedQuery.fullNameTokens.length === 0) return false;
  if (isExactNameMatchPrepared(candidate, preparedQuery)) return true;
  if (compoundNameFieldsMatchPrepared(candidate, preparedQuery)) return true;

  return allQueryTokensMatch(preparedQuery.fullNameTokens, normalizeFullName(candidate), threshold).matched;
}

/** How one name field of a registered record relates to the name a caller spoke. */
export enum NameFieldMatch {
  Exact = "exact",
  /** Speech-to-text spelled a name that sounds the same (Jon/John, Thomson/Thompson). */
  Homophone = "homophone",
  /** The caller used a curated nickname (Peggy/Margaret). Given name only. */
  Nickname = "nickname",
  /** EHR data entry added, dropped or changed a letter (Hollingworth/Hollingsworth). */
  Misentry = "misentry",
  /** A direct nickname of the spoken name matches a misentered registered name (Bill/Wiliam). Given name only. */
  NicknameMisentry = "nickname_misentry",
}

/** The name-difference checks that together explain a whole name, with each combination named. */
export enum NameMatchCheck {
  Exact = "exact",
  Misentry = "misentry",
  Homophone = "homophone",
  Nickname = "nickname",
  HomophoneMisentry = "homophone_misentry",
  NicknameMisentry = "nickname_misentry",
  NicknameHomophone = "nickname_homophone",
  NicknameHomophoneMisentry = "nickname_homophone_misentry",
}

export type NameFieldMatches = { givenName: NameFieldMatch; familyName: NameFieldMatch };

/**
 * Explains each name field of a record against a spoken name, or returns null when either field has no
 * explanation. Per field, the first relation that holds wins: exact, homophone, nickname (given name only),
 * misentry at the Strict threshold, then a misentry of a direct nickname (given name only). Callers decide
 * which checks their identity evidence supports.
 */
export function matchNameFields(candidate: NameQuery, query: NameQuery): NameFieldMatches | null {
  const givenName = matchGivenNameField(normalizeName(candidate.givenName), normalizeName(query.givenName));

  if (!givenName) return null;

  const familyName = matchNameField(normalizeName(candidate.familyName), normalizeName(query.familyName));

  return familyName ? { givenName, familyName } : null;
}

export function nameMatchCheck({ givenName, familyName }: NameFieldMatches): NameMatchCheck {
  const fields = [givenName, familyName];
  const nickname = fields.some(
    (field) => field === NameFieldMatch.Nickname || field === NameFieldMatch.NicknameMisentry
  );
  const homophone = fields.includes(NameFieldMatch.Homophone);
  const misentry = fields.some(
    (field) => field === NameFieldMatch.Misentry || field === NameFieldMatch.NicknameMisentry
  );

  if (nickname && homophone) {
    return misentry ? NameMatchCheck.NicknameHomophoneMisentry : NameMatchCheck.NicknameHomophone;
  }

  if (nickname) return misentry ? NameMatchCheck.NicknameMisentry : NameMatchCheck.Nickname;
  if (homophone) return misentry ? NameMatchCheck.HomophoneMisentry : NameMatchCheck.Homophone;

  return misentry ? NameMatchCheck.Misentry : NameMatchCheck.Exact;
}

/**
 * Homophone check for a speech-to-text spelling of the caller's name (Jon/John, Teresa/Theresa,
 * Newbury/Newberry): each field is exact or has the same number of tokens, each identical or sounding
 * alike under `soundsAlike`. That keeps vowel sounds, so names that differ by a vowel sound (John/Joan,
 * Carl/Carla) stay apart. Spellings that sound the same match even across genders (Toni/Tony, Jamie/Jaime).
 * No spelling distance is used; misentries are the separate Fuse check.
 */
export function isHomophoneNameMatch(candidate: NameQuery, query: NameQuery): boolean {
  const fields = matchNameFields(candidate, query);
  const check = fields && nameMatchCheck(fields);

  return check === NameMatchCheck.Exact || check === NameMatchCheck.Homophone;
}

function matchNameField(candidate: string, query: string): NameFieldMatch | null {
  if (!candidate || !query) return null;
  if (candidate === query) return NameFieldMatch.Exact;
  if (tokensSoundAlike(tokensFromNormalizedName(candidate), tokensFromNormalizedName(query))) {
    return NameFieldMatch.Homophone;
  }

  return isFieldMisentry(candidate, query) ? NameFieldMatch.Misentry : null;
}

function matchGivenNameField(candidate: string, query: string): NameFieldMatch | null {
  const match = matchNameField(candidate, query);

  if (match === NameFieldMatch.Exact || match === NameFieldMatch.Homophone) return match;
  if (!candidate || !query) return null;
  if (areDirectNicknames(candidate, query)) return NameFieldMatch.Nickname;
  if (match === NameFieldMatch.Misentry) return match;

  return directlyRelatedGivenNames(query).some((name) => isFieldMisentry(candidate, name))
    ? NameFieldMatch.NicknameMisentry
    : null;
}

function isFieldMisentry(candidate: string, query: string): boolean {
  return allQueryTokensMatch(tokensFromNormalizedName(query), candidate, FuzzyNameMatchThreshold.Strict).matched;
}

function tokensSoundAlike(candidateTokens: string[], queryTokens: string[]): boolean {
  return (
    candidateTokens.length === queryTokens.length &&
    queryTokens.every((token, index) => soundsAlike(token, candidateTokens[index]))
  );
}

/**
 * Generic name matcher that works for any type with name fields.
 * Exact first-and-last match takes priority; falls back to requiring every
 * query token to fuzzy-match at least one token in the stored full name.
 * Logs per-candidate token scores for observability.
 *
 * @param options.mergeTiers - Return matches from every tier (exact first) instead of only the first tier that hits.
 */
export function matchByName<T extends NameQuery>(
  candidates: T[],
  query: NameQuery,
  threshold = FuzzyNameMatchThreshold.Strict,
  { mergeTiers = false }: { mergeTiers?: boolean } = {}
): T[] {
  if (candidates.length === 0) return [];

  const preparedQuery = prepareNameQuery(query);
  const queryTokens = preparedQuery.fullNameTokens;

  if (queryTokens.length === 0) return [];

  const exactMatches = candidates.filter((p) => isExactNameMatchPrepared(p, preparedQuery));

  if (exactMatches.length > 0 && !mergeTiers) return exactMatches;

  const compoundNameMatches = candidates.filter(
    (p) => !exactMatches.includes(p) && compoundNameFieldsMatchPrepared(p, preparedQuery)
  );

  if (compoundNameMatches.length > 0 && !mergeTiers) return compoundNameMatches;

  const fuzzyMatches: T[] = [];
  const scoreLog: { scores: (number | undefined)[]; matched: boolean }[] = [];

  for (const p of candidates) {
    if (exactMatches.includes(p) || compoundNameMatches.includes(p)) continue;

    const { matched, scores } = allQueryTokensMatch(queryTokens, normalizeFullName(p), threshold);

    scoreLog.push({ scores, matched });
    if (matched) fuzzyMatches.push(p);
  }

  // Names stay out of this log: the redactor does not recognize these fields, and candidates can be
  // unrelated patients who share a DOB.
  logger.info("matchByName: fuzz scores", { candidates: scoreLog, threshold });

  return [...exactMatches, ...compoundNameMatches, ...fuzzyMatches];
}
